import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, Play, Pause, BarChart3, Edit, ChevronLeft, ChevronRight } from 'lucide-react';
import './ExperimentList.css';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'running', label: 'Running' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
  { value: 'archived', label: 'Archived' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'All Types' },
  { value: 'ab_test', label: 'A/B Test' },
  { value: 'multivariate', label: 'Multivariate' },
  { value: 'feature_flag', label: 'Feature Flag' },
];

function ExperimentList({ limit, embedded }) {
  const [experiments, setExperiments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = limit || 20;
  const navigate = useNavigate();

  const fetchExperiments = useCallback(async () => {
    try {
      setLoading(true);
      const params = { limit: pageSize, offset: (page - 1) * pageSize };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (siteFilter) params.site = siteFilter;
      if (searchText) params.search = searchText;
      const response = await axios.get('/api/experiments', { params });
      setExperiments(response.data.experiments || response.data || []);
      setTotalCount(response.data.total ?? (response.data.experiments || response.data || []).length);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch experiments:', err);
      setError('Failed to load experiments');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, siteFilter, searchText, page, pageSize]);

  useEffect(() => {
    fetchExperiments();
  }, [fetchExperiments]);

  const handleLifecycleAction = async (id, action, e) => {
    e.stopPropagation();
    e.preventDefault();
    try {
      await axios.post(`/api/experiments/${id}/lifecycle`, { action });
      fetchExperiments();
    } catch (err) {
      console.error(`Failed to ${action} experiment:`, err);
    }
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const getStatusClass = (status) => `exp-status-badge exp-status-${status}`;

  const getTypeBadge = (type) => {
    const labels = {
      ab_test: 'A/B Test',
      multivariate: 'Multivariate',
      feature_flag: 'Feature Flag',
    };
    return labels[type] || type;
  };

  const formatRate = (rate) => {
    if (rate == null) return '--';
    return (rate * 100).toFixed(1) + '%';
  };

  if (loading && experiments.length === 0) {
    return (
      <div className={`experiment-list ${embedded ? 'embedded' : ''}`}>
        <div className="exp-loading">
          <div className="spinner"></div>
          <p>Loading experiments...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`experiment-list ${embedded ? 'embedded' : ''}`}>
        <div className="exp-error">{error}</div>
      </div>
    );
  }

  return (
    <div className={`experiment-list ${embedded ? 'embedded' : ''}`}>
      {!embedded && (
        <div className="exp-list-filters">
          <div className="exp-search-wrapper">
            <Search size={16} className="exp-search-icon" />
            <input
              type="text"
              className="exp-search-input"
              placeholder="Search experiments..."
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
            />
          </div>
          <select
            className="exp-filter-select"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <select
            className="exp-filter-select"
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
          >
            {TYPE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
          <input
            type="text"
            className="exp-filter-input"
            placeholder="Site..."
            value={siteFilter}
            onChange={(e) => { setSiteFilter(e.target.value); setPage(1); }}
          />
        </div>
      )}

      {experiments.length === 0 ? (
        <div className="exp-empty">
          <p>No experiments found</p>
        </div>
      ) : (
        <>
          <table className="exp-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Site</th>
                <th>Variants</th>
                <th>Visitors</th>
                <th>Conv. Rate</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {experiments.map((exp) => (
                <tr
                  key={exp.id}
                  className="exp-table-row"
                  onClick={() => navigate(`/experiments/${exp.id}`)}
                >
                  <td>
                    <Link to={`/experiments/${exp.id}`} className="exp-name-link">
                      {exp.name}
                    </Link>
                    <div className="exp-key-text">{exp.key}</div>
                  </td>
                  <td>
                    <span className="exp-type-badge">{getTypeBadge(exp.type)}</span>
                  </td>
                  <td>
                    <span className={getStatusClass(exp.status)}>{exp.status}</span>
                  </td>
                  <td className="exp-site-cell">{exp.site || '--'}</td>
                  <td>{exp.variant_count ?? '--'}</td>
                  <td>{(exp.total_visitors ?? 0).toLocaleString()}</td>
                  <td>{formatRate(exp.conversion_rate)}</td>
                  <td>
                    <div className="exp-actions" onClick={(e) => e.stopPropagation()}>
                      {exp.status === 'draft' && (
                        <button
                          className="exp-action-btn"
                          title="Start"
                          onClick={(e) => handleLifecycleAction(exp.id, 'start', e)}
                        >
                          <Play size={14} />
                        </button>
                      )}
                      {exp.status === 'running' && (
                        <button
                          className="exp-action-btn"
                          title="Pause"
                          onClick={(e) => handleLifecycleAction(exp.id, 'pause', e)}
                        >
                          <Pause size={14} />
                        </button>
                      )}
                      {exp.status === 'paused' && (
                        <button
                          className="exp-action-btn"
                          title="Resume"
                          onClick={(e) => handleLifecycleAction(exp.id, 'resume', e)}
                        >
                          <Play size={14} />
                        </button>
                      )}
                      {(exp.status === 'running' || exp.status === 'completed') && (
                        <button
                          className="exp-action-btn"
                          title="View Results"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/experiments/${exp.id}/results`);
                          }}
                        >
                          <BarChart3 size={14} />
                        </button>
                      )}
                      <button
                        className="exp-action-btn"
                        title="Edit"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/experiments/${exp.id}/edit`);
                        }}
                      >
                        <Edit size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!embedded && totalPages > 1 && (
            <div className="exp-pagination">
              <button
                className="exp-page-btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
              >
                <ChevronLeft size={16} />
              </button>
              <span className="exp-page-info">
                Page {page} of {totalPages}
              </span>
              <button
                className="exp-page-btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ExperimentList;
